// Shared pure helpers + constants for the Preorders module. No React, no network calls
// (the axios fetchers live in PreordersModal.data.js). Split out of PreordersModal.js 2026-08-14.
import { getDisplayVariantTitle } from '../utils/variantTitle';
import { getApiBaseUrl } from '../utils/apiClient';

// Same as before in prod; locally it also honours a localhost REACT_APP_API_URL
// so a worktree frontend can talk to its own backend port.
const API_URL = getApiBaseUrl();

const isLocalHost = () =>
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1';

// URL tab slugs for bookmarkable state: /preorders?store=ccc&tab=ordered
// Index = selectedTab: 0 needs-ordering, 1 sent-to-supplier, 2 ordered, 3 backorders, 4 all (legacy), 5 archived.
const PREORDER_TAB_SLUGS = ['to-order', 'sent', 'ordered', 'backorder', 'all', 'archive'];
const PREORDER_STORE_VALUES = ['all', 'phaser', 'ccc'];


function getPreordersLocationState() {
  const defaults = { selectedStoreTab: 'phaser', selectedTab: 0 };
  const path = window.location.pathname || '';
  if (!path.startsWith('/preorders')) return defaults;

  const params = new URLSearchParams(window.location.search);
  const store = (params.get('store') || '').toLowerCase();
  const tabSlug = (params.get('tab') || '').toLowerCase();
  const tabIdx = PREORDER_TAB_SLUGS.indexOf(tabSlug);

  return {
    selectedStoreTab: PREORDER_STORE_VALUES.includes(store) ? store : defaults.selectedStoreTab,
    selectedTab: tabIdx !== -1 ? tabIdx : defaults.selectedTab
  };
}


function preordersRequestConfig(extra = {}) {
  const config = { withCredentials: true, ...extra };
  if (isLocalHost()) {
    config.headers = { ...config.headers, 'X-Local-Dev': 'localhost' };
  }
  return config;
}

/** Blank or warehouse/Shopify sentinel SKUs (product cache uses "N/A" for missing). */

function isMissingPreorderSku(sku) {
  const trimmed = String(sku || '').trim();
  if (!trimmed) return true;
  const upper = trimmed.toUpperCase();
  return upper === 'N/A' || upper === 'NA' || upper === 'NONE' || upper === '-' || upper === 'NULL';
}

function isNoSkuLineItem(item) {
  if (!item || typeof item !== 'object') return true;
  if (item.noSku) return true;
  return isMissingPreorderSku(item.sku);
}

function isPreorderNoSkuKey(value) {
  return String(value || '').startsWith('__nosku__');
}

/** Checkout tips ride along as a product-less "Tip" line — money, never stock to order. */
function isPreorderTipLine(item) {
  if (!item || typeof item !== 'object') return false;
  if (!isMissingPreorderSku(item.sku)) return false;
  return /^tip$/i.test(String(item.title || item.name || '').trim());
}

function getUniquePreorderSkus(orderList = []) {
  const byUpperSku = new Map();
  (orderList || []).forEach(order => {
    (order.lineItems || []).forEach(item => {
      const sku = String(item?.sku || '').trim();
      if (!sku || isMissingPreorderSku(sku)) return;
      const key = sku.toUpperCase();
      if (!byUpperSku.has(key)) byUpperSku.set(key, sku);
    });
  });
  return [...byUpperSku.values()];
}

function getPreorderOrderStore(order) {
  if (order?.storeOverride) return order.storeOverride;
  if (order?.store) return order.store;
  return String(order?.orderNumber || '').toUpperCase().startsWith('CCC') ? 'central-coast-coffee' : 'phaser-fpv';
}

function getUniqueNoSkuTitleLookups(orderList = []) {
  const byKey = new Map();
  (orderList || []).forEach(order => {
    const store = getPreorderOrderStore(order);
    (order.lineItems || []).forEach(item => {
      const title = String(item?.title || '').trim();
      if (!isNoSkuLineItem(item) || !title) return;
      const lookupKey = getPreorderItemSkuKey(item);
      const key = `${store}::${lookupKey.toUpperCase()}`;
      if (!byKey.has(key)) byKey.set(key, { title, store, lookupKey });
    });
  });
  return [...byKey.values()];
}

function mapProductsBySku(products = []) {
  const bySku = {};
  (products || []).forEach(product => {
    const key = String(product?.lookupKey || product?.sku || '').trim().toUpperCase();
    if (!key) return;
    if (!bySku[key]) bySku[key] = [];
    bySku[key].push(product);
  });
  return bySku;
}


function getShopifyOrderStatusCacheKey(order) {
  const orderNumber = String(order?.orderNumber || '').trim();
  if (!orderNumber) return '';
  return `${getPreorderOrderStore(order)}::${orderNumber.toUpperCase()}`;
}

function getUniquePreorderOrderLookups(orderList = []) {
  const byKey = new Map();
  (orderList || []).forEach(order => {
    const orderNumber = String(order?.orderNumber || '').trim();
    if (!orderNumber) return;
    const store = getPreorderOrderStore(order);
    const cacheKey = getShopifyOrderStatusCacheKey(order);
    if (!cacheKey || byKey.has(cacheKey)) return;
    byKey.set(cacheKey, {
      cacheKey,
      orderNumber,
      orderId: String(order?.orderId || order?.id || '').trim(),
      store
    });
  });
  return [...byKey.values()];
}


function formatShopifyFulfillmentStatus(status) {
  const normalized = String(status || '').trim().toUpperCase();
  const labels = {
    UNFULFILLED: 'Unfulfilled',
    PARTIALLY_FULFILLED: 'Partially fulfilled',
    FULFILLED: 'Fulfilled',
    IN_PROGRESS: 'In progress',
    ON_HOLD: 'On hold',
    OPEN: 'Open',
    PENDING_FULFILLMENT: 'Pending fulfillment',
    RESTOCKED: 'Restocked',
    SCHEDULED: 'Scheduled'
  };
  return labels[normalized] || (status ? String(status).replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase()) : 'Unknown');
}

function getShopifyFulfillmentStatusClass(status) {
  const normalized = String(status || '').trim().toUpperCase();
  if (normalized === 'FULFILLED') return 'PreordersModal-shopifyStatusChip--fulfilled';
  if (normalized === 'PARTIALLY_FULFILLED' || normalized === 'IN_PROGRESS' || normalized === 'SCHEDULED') {
    return 'PreordersModal-shopifyStatusChip--partial';
  }
  if (normalized === 'UNFULFILLED' || normalized === 'OPEN' || normalized === 'PENDING_FULFILLMENT') {
    return 'PreordersModal-shopifyStatusChip--pending';
  }
  return 'PreordersModal-shopifyStatusChip--neutral';
}

function isShopifyStatusHidden(shopifyStatus) {
  if (!shopifyStatus || shopifyStatus.error) return true;
  if (shopifyStatus.cancelled) return false;
  const normalized = String(shopifyStatus.fulfillmentStatus || '').trim().toUpperCase();
  return !normalized || normalized === 'UNFULFILLED';
}

function getShopifyStatusHighlight(shopifyStatus) {
  if (!shopifyStatus || shopifyStatus.error || shopifyStatus.cancelled) return null;
  // A till-fulfilled POS line is NOT done, and without the till detail we
  // can't tell — never let "Delete fulfilled" sweep either.
  if (shopifyStatus.posSplitNeeded) return null;
  if (String(shopifyStatus.sourceName || '').trim().toLowerCase() === 'pos'
    && !Array.isArray(shopifyStatus.posSaleFulfilledSkus)) return null;
  const status = String(shopifyStatus.fulfillmentStatus || '').trim().toUpperCase();
  if (status !== 'FULFILLED') return null;
  return {
    label: 'Fulfilled',
    tracking: (shopifyStatus.tracking || []).filter(entry => String(entry?.number || '').trim())
  };
}

function getShopifyStatusDisplay(shopifyStatus) {
  if (shopifyStatus?.posSplitNeeded) {
    return {
      label: 'Fulfilled at POS · split needed',
      chipClass: 'PreordersModal-shopifyStatusChip--cancelled',
      title: 'The till marked this preorder item fulfilled, so the customer sees "Fulfilled". In Shopify admin, cancel the fulfilment and re-fulfil only the items they took.',
      tracking: []
    };
  }
  if (isShopifyStatusHidden(shopifyStatus)) return null;
  if (shopifyStatus.cancelled) {
    return {
      label: 'Cancelled',
      chipClass: 'PreordersModal-shopifyStatusChip--cancelled',
      tracking: []
    };
  }
  const tracking = (shopifyStatus.tracking || []).filter(entry => String(entry?.number || '').trim());
  return {
    label: formatShopifyFulfillmentStatus(shopifyStatus.fulfillmentStatus),
    chipClass: getShopifyFulfillmentStatusClass(shopifyStatus.fulfillmentStatus),
    tracking
  };
}

// A partially fulfilled order carries per-line Shopify fulfilment (`lines`).
// When every Shopify line matching this preorder line has shipped, report the
// line itself as FULFILLED; unshipped lines keep the order's partial status.
function getPreorderLineShopifyStatus(shopifyStatus, item) {
  if (!shopifyStatus || shopifyStatus.error || shopifyStatus.cancelled) return shopifyStatus;
  const norm = value => String(value || '').trim().toUpperCase();
  const sku = norm(item?.sku);
  // POS can't split a sale, so the till fulfils the preorder item too
  // (backend services/shopifyOrderStatus posSaleFulfilledSkus).
  // Same bare/-DG equivalence as backend poPreorderAllocation shopifySkuMatches.
  const stripDg = value => value.replace(/-DG$/, '');
  if (sku && Array.isArray(shopifyStatus.posSaleFulfilledSkus)
    && shopifyStatus.posSaleFulfilledSkus.some(fulfilledSku => stripDg(norm(fulfilledSku)) === stripDg(sku))) {
    return { ...shopifyStatus, posSplitNeeded: true };
  }
  if (!Array.isArray(shopifyStatus.lines)) return shopifyStatus;
  if (String(shopifyStatus.fulfillmentStatus || '').trim().toUpperCase() !== 'PARTIALLY_FULFILLED') return shopifyStatus;
  const title = norm(item?.title);
  if (!sku && !title) return shopifyStatus;
  const matches = shopifyStatus.lines.filter(line => (sku
    ? norm(line.sku) === sku
    : norm(line.title) === title || norm(line.name) === title));
  if (matches.length === 0 || matches.some(line => Number(line.unfulfilledQuantity) > 0)) return shopifyStatus;
  return { ...shopifyStatus, fulfillmentStatus: 'FULFILLED' };
}

function getPreorderLineOrderId(order, item) {
  const parentOrderId = String(order?.orderId ?? order?.id ?? '').trim();
  if (order?.isMergedOrder) return String(item?.orderId ?? parentOrderId).trim();
  return parentOrderId;
}

function collectFulfilledPreorderLineItems(orderList, shopifyOrderStatusByNumber, isLineArchived, getOrderStore) {
  const items = [];
  (orderList || []).forEach(order => {
    const store = getOrderStore(order);
    (order.lineItems || []).forEach(item => {
      const orderId = getPreorderLineOrderId(order, item);
      const skuKey = getPreorderItemSkuKey(item);
      if (!orderId || isLineArchived(orderId, skuKey)) return;
      const statusKey = getShopifyOrderStatusCacheKey({ ...order, store });
      const shopifyStatus = getPreorderLineShopifyStatus(shopifyOrderStatusByNumber[statusKey], item);
      // Cancelled Shopify orders are dead preorders too: sweep them with the
      // fulfilled ones (whole orders or individually shipped lines) so they
      // stop sitting in "Needs ordering".
      const cancelled = Boolean(shopifyStatus && !shopifyStatus.error && shopifyStatus.cancelled);
      if (!cancelled && !getShopifyStatusHighlight(shopifyStatus)) return;
      items.push({
        orderId,
        sku: item?.sku ?? null,
        title: item?.title ?? null,
        skuKey,
        cancelled
      });
    });
  });
  return items;
}

/** Order numbers whose POS sale fulfilled a live preorder line (split needed in Shopify). */
function collectPosSplitNeededOrderNumbers(orderList, shopifyOrderStatusByNumber, isLineArchived, getOrderStore) {
  const orderNumbers = new Set();
  (orderList || []).forEach(order => {
    const store = getOrderStore(order);
    (order.lineItems || []).forEach(item => {
      const orderId = getPreorderLineOrderId(order, item);
      if (!orderId || isLineArchived(orderId, getPreorderItemSkuKey(item))) return;
      const statusKey = getShopifyOrderStatusCacheKey({ ...order, store });
      if (getPreorderLineShopifyStatus(shopifyOrderStatusByNumber[statusKey], item)?.posSplitNeeded) {
        orderNumbers.add(String(order.orderNumber || orderId));
      }
    });
  });
  return [...orderNumbers];
}

function getMissingProductLookups(orderList, existingCache = {}) {
  const skus = getUniquePreorderSkus(orderList);
  const titleLookups = getUniqueNoSkuTitleLookups(orderList);
  const missingSkus = skus.filter(sku => {
    const cachedProducts = existingCache[String(sku || '').toUpperCase()];
    if (!Array.isArray(cachedProducts) || cachedProducts.length === 0) return true;
    return cachedProducts.every(product =>
      !String(product?.productType || '').trim() || !Object.prototype.hasOwnProperty.call(product, 'variantTitle')
    );
  });
  const missingTitleLookups = titleLookups.filter(({ lookupKey }) => {
    const cachedProducts = existingCache[String(lookupKey || '').toUpperCase()];
    if (!Array.isArray(cachedProducts) || cachedProducts.length === 0) return true;
    return cachedProducts.every(product =>
      !String(product?.productType || '').trim() || !Object.prototype.hasOwnProperty.call(product, 'variantTitle')
    );
  });

  return { missingSkus, missingTitleLookups };
}


const SHOPIFY_ADMIN_URLS = {
  'phaser-fpv': 'https://admin.shopify.com/store/phaser-fpv/orders/',
  'central-coast-coffee': 'https://admin.shopify.com/store/central-coast-coffee/orders/'
};

const SHOPIFY_PRODUCT_ADMIN_BASE = {
  'phaser-fpv': 'https://admin.shopify.com/store/phaser-fpv/products/',
  'central-coast-coffee': 'https://admin.shopify.com/store/central-coast-coffee/products/'
};

const URGENT_DAYS = 4;
const CRITICAL_DAYS = 7;

function getPreorderItemSkuKey(item) {
  const sku = String(item?.sku || '').trim();
  if (sku && !isMissingPreorderSku(sku)) return sku;
  const title = String(item?.title || 'Unknown Product').trim();
  return `__nosku__${title || 'Unknown Product'}`;
}

function getPreorderLineArchivedKey(orderId, itemOrSku, title = '') {
  const orderIdStr = String(orderId || '').trim();
  if (!orderIdStr) return '';
  if (itemOrSku && typeof itemOrSku === 'object') {
    return `${orderIdStr}__${getPreorderItemSkuKey(itemOrSku)}`;
  }
  const skuText = String(itemOrSku || '').trim();
  if (skuText && !isMissingPreorderSku(skuText) && !isPreorderNoSkuKey(skuText)) {
    return `${orderIdStr}__${skuText}`;
  }
  return `${orderIdStr}__${getPreorderItemSkuKey({ sku: null, title: String(title || skuText.replace(/^__nosku__/i, '') || '').trim() })}`;
}

function resolvePreorderPoLink(orderId, sku, poLinks = {}) {
  const skuTrim = String(sku || '').trim();
  const orderIdTrim = String(orderId || '').trim();
  if (!skuTrim) return { link: null, linkKey: null };

  if (orderIdTrim) {
    const lineKey = `${orderIdTrim}__${skuTrim}`;
    if (poLinks[lineKey]) return { link: poLinks[lineKey], linkKey: lineKey };
  }

  if (poLinks[skuTrim]) return { link: poLinks[skuTrim], linkKey: skuTrim };

  const skuLower = skuTrim.toLowerCase();
  for (const [key, link] of Object.entries(poLinks)) {
    const sep = key.indexOf('__');
    const parsedOrderId = sep > 0 ? key.slice(0, sep) : null;
    const parsedSku = sep > 0 ? key.slice(sep + 2) : key;
    if (!parsedOrderId && parsedSku.toLowerCase() === skuLower) {
      return { link, linkKey: key };
    }
  }

  return { link: null, linkKey: null };
}

function pickProductForStore(products, store) {
  if (!products?.length) return null;
  const orderIsCCC =
    store === 'central-coast-coffee' || (store || '').toLowerCase().includes('ccc');
  return products.find(p => {
    const sn = (p.storeDisplayName || p.store || '').toLowerCase();
    if (orderIsCCC) return sn.includes('coast') || sn === 'ccc';
    return sn.includes('phaser') || sn.includes('fpv');
  }) || products[0];
}

function splitTrailingVariantFromTitle(rawTitle) {
  const title = String(rawTitle || '').trim();
  if (!title) return { title: '', variant: null };
  const idx = title.lastIndexOf(' - ');
  if (idx > 0) {
    const base = title.slice(0, idx).trim();
    const variant = title.slice(idx + 3).trim();
    if (base && variant && variant.length <= 60) {
      return { title: base, variant };
    }
  }
  return { title, variant: null };
}

function formatPreorderProductTitle({ sku, productCache, store, rawTitle = null }) {
  if (isPreorderNoSkuKey(sku) || isMissingPreorderSku(sku) || (!String(sku || '').trim() && rawTitle)) {
    const raw = rawTitle || String(sku || '').replace('__nosku__', '');
    const { title, variant } = splitTrailingVariantFromTitle(raw);
    const displayVariant = getDisplayVariantTitle(variant, title);
    if (displayVariant) return `${title} (${displayVariant})`;
    return title || 'Unknown Product';
  }

  const key = (sku || '').toUpperCase();
  const products = productCache?.[key];
  if (!products?.length) return rawTitle || sku || 'Unknown Product';

  const product = pickProductForStore(products, store);
  const title = product?.title || product?.productTitle || sku;
  const variant = getDisplayVariantTitle(product?.variantTitle, title);
  if (variant) return `${title} (${variant})`;
  return title;
}

function isManualPreorderOrder(order) {
  const orderId = String(order?.orderId ?? order?.id ?? '').trim();
  return orderId.startsWith('manual-');
}

function getPreorderOrderSubline(order, { isArchived, isBackorder, isOrdered, isSent }) {
  if (isArchived) return 'Archived preorder';
  if (isBackorder) return 'Backorder';
  if (isOrdered) return 'Ordered';
  if (isSent) return 'Sent to supplier';
  return isManualPreorderOrder(order) ? 'Manual preorder' : 'Shopify order';
}

export {
  API_URL,
  isLocalHost,
  PREORDER_TAB_SLUGS,
  PREORDER_STORE_VALUES,
  getPreordersLocationState,
  preordersRequestConfig,
  isMissingPreorderSku,
  isNoSkuLineItem,
  isPreorderNoSkuKey,
  isPreorderTipLine,
  getUniquePreorderSkus,
  getPreorderOrderStore,
  getUniqueNoSkuTitleLookups,
  mapProductsBySku,
  getShopifyOrderStatusCacheKey,
  getUniquePreorderOrderLookups,
  formatShopifyFulfillmentStatus,
  getShopifyFulfillmentStatusClass,
  isShopifyStatusHidden,
  getShopifyStatusHighlight,
  getShopifyStatusDisplay,
  getPreorderLineShopifyStatus,
  getPreorderLineOrderId,
  collectFulfilledPreorderLineItems,
  collectPosSplitNeededOrderNumbers,
  getMissingProductLookups,
  SHOPIFY_ADMIN_URLS,
  SHOPIFY_PRODUCT_ADMIN_BASE,
  URGENT_DAYS,
  CRITICAL_DAYS,
  getPreorderItemSkuKey,
  getPreorderLineArchivedKey,
  resolvePreorderPoLink,
  pickProductForStore,
  splitTrailingVariantFromTitle,
  formatPreorderProductTitle,
  isManualPreorderOrder,
  getPreorderOrderSubline
};